import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card, Tag, Button, Space, Row, Col, message, Typography, Spin, Avatar, Progress, Empty, Divider
} from 'antd';
import {
    ArrowLeftOutlined, CheckCircleOutlined, ClockCircleOutlined,
    CarOutlined, DollarOutlined, PhoneOutlined, CalendarOutlined,
    TrophyOutlined, WalletOutlined, EnvironmentOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Configuration des paliers avec seuils et montants dynamiques par ville
const getAffiliateMilestones = (t = {}, r = {}) => {
    const aff = r.affiliate || {};
    return [
        { key: 'accountCreation', type: 'account_creation', amount: aff.accountCreation || 500, label: 'Création du compte', icon: '1', targetRides: null, targetHours: null },
        { key: 'rides5', type: 'activation_rides_5', amount: aff.rides5 || 900, label: `${t.milestoneThreshold1 || 5} courses complétées`, icon: '2', targetRides: t.milestoneThreshold1 || 5, targetHours: null },
        { key: 'rides15', type: 'activation_rides_15', amount: aff.rides15 || 1350, label: `${t.milestoneThreshold2 || 15} courses complétées`, icon: '3', targetRides: t.milestoneThreshold2 || 15, targetHours: null },
        { key: 'rides35', type: 'activation_rides_35', amount: aff.rides35 || 1800, label: `${t.milestoneThreshold3 || 35} courses complétées`, icon: '4', targetRides: t.milestoneThreshold3 || 35, targetHours: null },
        { key: 'rides55', type: 'activation_rides_55', amount: aff.rides55 || 1800, label: `${t.milestoneThreshold4 || 55} courses complétées`, icon: '5', targetRides: t.milestoneThreshold4 || 55, targetHours: null },
    ];
};

const getAffiliateReactivationMilestones = (r = {}) => {
    const aff = r.affiliate || {};
    return [
        { key: 'accountCreation', type: 'account_creation', amount: aff.accountCreation || 500, label: 'Création du compte', icon: '1', targetRides: null, targetHours: null },
        { key: 'rides20Reactivation', type: 'reactivation_rides_20', amount: aff.reactivationRides20 || 1500, label: '20 courses complétées', icon: '2', targetRides: 20, targetHours: null },
        { key: 'hours130', type: 'reactivation_hours_130', amount: aff.reactivationHours130 || 900, label: '130 heures complétées', icon: '3', targetRides: null, targetHours: 130 },
    ];
};

const getAmbassadorMilestones = (t = {}, r = {}) => {
    const amb = r.ambassador || {};
    return [
        { key: 'accountCreation', type: 'account_creation', amount: amb.accountCreation || 50, label: 'Création du compte', icon: '1', targetRides: null, targetHours: null },
        { key: 'rides5', type: 'activation_rides_5', amount: amb.rides5 || 100, label: `${t.milestoneThreshold1 || 5} courses complétées`, icon: '2', targetRides: t.milestoneThreshold1 || 5, targetHours: null },
        { key: 'rides15', type: 'activation_rides_15', amount: amb.rides15 || 150, label: `${t.milestoneThreshold2 || 15} courses complétées`, icon: '3', targetRides: t.milestoneThreshold2 || 15, targetHours: null },
        { key: 'rides35', type: 'activation_rides_35', amount: amb.rides35 || 200, label: `${t.milestoneThreshold3 || 35} courses complétées`, icon: '4', targetRides: t.milestoneThreshold3 || 35, targetHours: null },
        { key: 'rides55', type: 'activation_rides_55', amount: amb.rides55 || 200, label: `${t.milestoneThreshold4 || 55} courses complétées`, icon: '5', targetRides: t.milestoneThreshold4 || 55, targetHours: null },
    ];
};

const getAmbassadorReactivationMilestones = (r = {}) => {
    const amb = r.ambassador || {};
    return [
        { key: 'accountCreation', type: 'account_creation', amount: amb.accountCreation || 50, label: 'Création du compte', icon: '1', targetRides: null, targetHours: null },
        { key: 'rides20Reactivation', type: 'reactivation_rides_20', amount: amb.reactivationRides20 || 150, label: '20 courses complétées', icon: '2', targetRides: 20, targetHours: null },
        { key: 'hours130', type: 'reactivation_hours_130', amount: amb.reactivationHours130 || 100, label: '130 heures complétées', icon: '3', targetRides: null, targetHours: 130 },
    ];
};

// Paliers pour chauffeurs directs de l'ambassadeur (affiliate + ambassador combinés)
const getAmbassadorDirectMilestones = (t = {}, r = {}) => {
    const aff = r.affiliate || {};
    const amb = r.ambassador || {};
    return [
        { key: 'accountCreation', type: 'account_creation', amount: (aff.accountCreation || 500) + (amb.accountCreation || 50), affiliateAmount: aff.accountCreation || 500, ambassadorAmount: amb.accountCreation || 50, label: 'Création du compte', icon: '1', targetRides: null, targetHours: null },
        { key: 'rides5', type: 'activation_rides_5', amount: (aff.rides5 || 900) + (amb.rides5 || 100), affiliateAmount: aff.rides5 || 900, ambassadorAmount: amb.rides5 || 100, label: `${t.milestoneThreshold1 || 5} courses complétées`, icon: '2', targetRides: t.milestoneThreshold1 || 5, targetHours: null },
        { key: 'rides15', type: 'activation_rides_15', amount: (aff.rides15 || 1350) + (amb.rides15 || 150), affiliateAmount: aff.rides15 || 1350, ambassadorAmount: amb.rides15 || 150, label: `${t.milestoneThreshold2 || 15} courses complétées`, icon: '3', targetRides: t.milestoneThreshold2 || 15, targetHours: null },
        { key: 'rides35', type: 'activation_rides_35', amount: (aff.rides35 || 1800) + (amb.rides35 || 200), affiliateAmount: aff.rides35 || 1800, ambassadorAmount: amb.rides35 || 200, label: `${t.milestoneThreshold3 || 35} courses complétées`, icon: '4', targetRides: t.milestoneThreshold3 || 35, targetHours: null },
        { key: 'rides55', type: 'activation_rides_55', amount: (aff.rides55 || 1800) + (amb.rides55 || 200), affiliateAmount: aff.rides55 || 1800, ambassadorAmount: amb.rides55 || 200, label: `${t.milestoneThreshold4 || 55} courses complétées`, icon: '5', targetRides: t.milestoneThreshold4 || 55, targetHours: null },
    ];
};

const getAmbassadorDirectReactivationMilestones = (r = {}) => {
    const aff = r.affiliate || {};
    const amb = r.ambassador || {};
    return [
        { key: 'accountCreation', type: 'account_creation', amount: (aff.accountCreation || 500) + (amb.accountCreation || 50), affiliateAmount: aff.accountCreation || 500, ambassadorAmount: amb.accountCreation || 50, label: 'Création du compte', icon: '1', targetRides: null, targetHours: null },
        { key: 'rides20Reactivation', type: 'reactivation_rides_20', amount: (aff.reactivationRides20 || 1500) + (amb.reactivationRides20 || 150), affiliateAmount: aff.reactivationRides20 || 1500, ambassadorAmount: amb.reactivationRides20 || 150, label: '20 courses complétées', icon: '2', targetRides: 20, targetHours: null },
        { key: 'hours130', type: 'reactivation_hours_130', amount: (aff.reactivationHours130 || 900) + (amb.reactivationHours130 || 100), affiliateAmount: aff.reactivationHours130 || 900, ambassadorAmount: amb.reactivationHours130 || 100, label: '130 heures complétées', icon: '3', targetRides: null, targetHours: 130 },
    ];
};

const DriverCommissionDetailPage = () => {
    const { commissionId, driverId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [driver, setDriver] = useState(null);
    const [recipientType, setRecipientType] = useState('affiliate');
    const [isDirect, setIsDirect] = useState(false);
    const [milestoneThresholds, setMilestoneThresholds] = useState({});
    const [commissionRates, setCommissionRates] = useState({});

    useEffect(() => {
        const type = searchParams.get('type');
        const directParam = searchParams.get('isDirect');
        if (type) setRecipientType(type);
        if (directParam === 'true') setIsDirect(true);
        fetchDriverDetails();
    }, [driverId, commissionId]);

    const fetchDriverDetails = async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.getDriverCommissionDetails(commissionId, driverId);
            if (response.success) {
                setDriver(response.data);
                if (response.data.recipientType) {
                    setRecipientType(response.data.recipientType);
                }
                // Détecter si c'est un chauffeur direct via les données du serveur
                if (response.data.isDirect !== undefined) {
                    setIsDirect(response.data.isDirect);
                }
                if (response.data.milestoneThresholds) {
                    setMilestoneThresholds(response.data.milestoneThresholds);
                }
                if (response.data.commissionRates) {
                    setCommissionRates(response.data.commissionRates);
                }
            } else {
                message.error('Chauffeur non trouvé');
                navigate(-1);
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const getMilestones = () => {
        const isReactivation = driver?.driverType === 'reactivation';

        if (recipientType === 'ambassador') {
            if (isDirect) {
                return isReactivation ? getAmbassadorDirectReactivationMilestones(commissionRates) : getAmbassadorDirectMilestones(milestoneThresholds, commissionRates);
            }
            return isReactivation ? getAmbassadorReactivationMilestones(commissionRates) : getAmbassadorMilestones(milestoneThresholds, commissionRates);
        }
        return isReactivation ? getAffiliateReactivationMilestones(commissionRates) : getAffiliateMilestones(milestoneThresholds, commissionRates);
    };

    const getCommissionForMilestone = (milestoneKey, milestoneType) => {
        if (!driver?.commissions) return null;
        const typeMapping = {
            'accountCreation': ['account_creation', 'referral_bonus'],
            'rides5': ['activation_rides_5'],
            'rides15': ['activation_rides_15'],
            'rides35': ['activation_rides_35'],
            'rides55': ['activation_rides_55'],
            'rides20Reactivation': ['reactivation_rides_20'],
            'hours130': ['hours_130', 'reactivation_hours_130'],
        };
        const types = typeMapping[milestoneKey] || [milestoneType];
        return driver.commissions.find(c => types.includes(c.type));
    };

    const getStatusConfig = (milestone) => {
        const achieved = driver?.milestones?.[milestone.key] || false;
        const commission = getCommissionForMilestone(milestone.key, milestone.type);

        if (commission?.status === 'paid') {
            return { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', text: 'Payé', icon: <CheckCircleOutlined /> };
        } else if (commission?.status === 'approved') {
            return { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', text: 'Approuvé', icon: <ClockCircleOutlined /> };
        } else if (commission?.status === 'pending' || commission?.status === 'pending_validation') {
            return { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', text: 'En attente', icon: <ClockCircleOutlined /> };
        } else if (achieved) {
            return { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', text: 'À payer', icon: <DollarOutlined /> };
        }
        return { color: '#bfbfbf', bg: '#fafafa', border: '#d9d9d9', text: 'Non atteint', icon: <ClockCircleOutlined /> };
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!driver) {
        return <Empty description="Chauffeur non trouvé" />;
    }

    const milestones = getMilestones();
    const totalPotential = milestones.reduce((sum, m) => sum + m.amount, 0);
    const achievedCount = driver.milestonesCount || 0;
    const totalMilestones = milestones.length;
    const progressPercent = Math.round((achievedCount / totalMilestones) * 100);
    const earnedAmount = driver.totalRevenue || 0;

    // Stats courses actuelles
    const currentRides = driver.currentStats?.totalRides || 0;
    const activationWindowEnd = driver.activationWindowEnd ? dayjs(driver.activationWindowEnd) : null;
    const now = dayjs();
    const daysRemaining = activationWindowEnd ? Math.max(0, activationWindowEnd.diff(now, 'day', true)) : null;
    const hoursRemaining = activationWindowEnd ? Math.max(0, activationWindowEnd.diff(now, 'hour')) % 24 : null;
    const isWindowExpired = activationWindowEnd ? now.isAfter(activationWindowEnd) : false;

    return (
        <div style={{ padding: '16px', maxWidth: 1200, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 16,
                marginBottom: 24
            }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(-1)}
                    style={{ borderRadius: 8 }}
                />
                <div style={{ flex: 1 }}>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                        Détails du chauffeur
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Inscrit le {dayjs(driver.createdAt).format('DD MMM YYYY')}
                    </Text>
                </div>
            </div>

            <Row gutter={[20, 20]}>
                {/* Colonne gauche - Infos chauffeur */}
                <Col xs={24} lg={8}>
                    {/* Carte Chauffeur */}
                    <Card
                        style={{
                            borderRadius: 12,
                            marginBottom: 20,
                            border: '2px solid #fff7e620',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: 0 }}
                    >
                        <div style={{
                            background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
                            padding: 24,
                            textAlign: 'center'
                        }}>
                            <Avatar
                                size={72}
                                style={{ background: '#fa8c16', marginBottom: 12 }}
                                icon={<CarOutlined />}
                            />
                            <div style={{ fontWeight: 700, fontSize: 20, color: '#262626' }}>
                                {driver.firstName} {driver.lastName}
                            </div>
                            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Tag color="orange" style={{ borderRadius: 12 }}>
                                    Chauffeur
                                </Tag>
                                {isDirect && (
                                    <Tag color="purple" style={{ borderRadius: 12 }}>
                                        Direct
                                    </Tag>
                                )}
                            </div>
                        </div>
                        <div style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                <PhoneOutlined style={{ color: '#8c8c8c' }} />
                                <span style={{ color: '#595959' }}>{driver.phone}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                <EnvironmentOutlined style={{ color: '#8c8c8c' }} />
                                <span style={{ color: '#595959' }}>{driver.city?.name || '-'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <CalendarOutlined style={{ color: '#8c8c8c' }} />
                                <span style={{ color: '#595959' }}>{dayjs(driver.createdAt).format('DD/MM/YYYY')}</span>
                            </div>
                        </div>
                    </Card>

                    {/* Stats Cards */}
                    <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                        <Col span={12}>
                            <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                <TrophyOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#262626' }}>
                                    {achievedCount}<span style={{ fontSize: 16, color: '#8c8c8c' }}>/{totalMilestones}</span>
                                </div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Paliers atteints</div>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                <WalletOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                                <div style={{ fontSize: 20, fontWeight: 700, color: '#1890ff' }}>
                                    {totalPotential.toLocaleString()}
                                </div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Potentiel (F)</div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Progression Courses */}
                    <Card
                        style={{
                            borderRadius: 12,
                            marginBottom: 20,
                            border: isWindowExpired ? '1px solid #ff4d4f' : '1px solid #1890ff'
                        }}
                        bodyStyle={{ padding: 20 }}
                    >
                        <div style={{ textAlign: 'center' }}>
                            <CarOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                            <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>Courses effectuées</Text>
                            <div style={{ fontSize: 36, fontWeight: 700, color: '#1890ff', margin: '8px 0' }}>
                                {currentRides} <span style={{ fontSize: 16, color: '#8c8c8c' }}>courses</span>
                            </div>
                            <Progress
                                percent={Math.min(100, Math.round((currentRides / (driver?.driverType === 'reactivation' ? 20 : (milestoneThresholds.milestoneThreshold4 || 55))) * 100))}
                                strokeColor={{ '0%': '#1890ff', '100%': '#69c0ff' }}
                                trailColor="#f0f0f0"
                                strokeWidth={10}
                                format={() => `${currentRides}/${driver?.driverType === 'reactivation' ? 20 : (milestoneThresholds.milestoneThreshold4 || 55)}`}
                            />
                            {activationWindowEnd && (
                                <div style={{
                                    marginTop: 12,
                                    padding: '8px 12px',
                                    borderRadius: 8,
                                    background: isWindowExpired ? '#fff2f0' : '#e6f7ff'
                                }}>
                                    <ClockCircleOutlined style={{
                                        marginRight: 6,
                                        color: isWindowExpired ? '#ff4d4f' : '#1890ff'
                                    }} />
                                    {isWindowExpired ? (
                                        <Text style={{ color: '#ff4d4f', fontSize: 12 }}>
                                            Fenêtre de {driver?.driverType === 'reactivation' ? '2' : '7'} jours expirée
                                        </Text>
                                    ) : (
                                        <Text style={{ color: '#1890ff', fontSize: 12 }}>
                                            <strong>{Math.floor(daysRemaining)}j {hoursRemaining}h</strong> restants
                                        </Text>
                                    )}
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Revenus générés */}
                    <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 13 }}>Commissions générées</Text>
                            <div style={{ fontSize: 36, fontWeight: 700, color: '#52c41a', margin: '8px 0' }}>
                                {earnedAmount.toLocaleString()} <span style={{ fontSize: 16 }}>F</span>
                            </div>
                            <Progress
                                percent={progressPercent}
                                strokeColor={{ '0%': '#52c41a', '100%': '#95de64' }}
                                trailColor="#f0f0f0"
                                strokeWidth={10}
                                style={{ marginTop: 12 }}
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {achievedCount} palier{achievedCount > 1 ? 's' : ''} sur {totalMilestones} atteint{achievedCount > 1 ? 's' : ''}
                            </Text>
                        </div>
                    </Card>
                </Col>

                {/* Colonne droite - Paliers */}
                <Col xs={24} lg={16}>
                    <Card
                        style={{ borderRadius: 12, height: '100%' }}
                        title={
                            <div>
                                <span style={{ fontWeight: 600, fontSize: 16 }}>Progression des paliers</span>
                                {isDirect && (
                                    <div style={{ fontSize: 12, fontWeight: 400, color: '#722ed1', marginTop: 4 }}>
                                        Commissions combinées (Affilié + Ambassadeur)
                                    </div>
                                )}
                            </div>
                        }
                        bodyStyle={{ padding: 0 }}
                    >
                        {milestones.map((milestone, index) => {
                            const achieved = driver?.milestones?.[milestone.key] || false;
                            const statusConfig = getStatusConfig(milestone);

                            // Calcul de la progression pour les paliers de courses
                            const targetRides = milestone.targetRides;
                            const rideProgress = targetRides ? Math.min(100, Math.round((currentRides / targetRides) * 100)) : null;

                            return (
                                <div
                                    key={milestone.key}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '16px 20px',
                                        borderBottom: index < milestones.length - 1 ? '1px solid #f0f0f0' : 'none',
                                        background: achieved ? '#fafffe' : '#fff',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {/* Numéro / Check */}
                                    <div style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        background: achieved ? '#52c41a' : '#f0f0f0',
                                        color: achieved ? '#fff' : '#8c8c8c',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        fontSize: 16,
                                        marginRight: 16,
                                        flexShrink: 0
                                    }}>
                                        {achieved ? <CheckCircleOutlined /> : milestone.icon}
                                    </div>

                                    {/* Infos */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontWeight: 600,
                                            fontSize: 15,
                                            color: achieved ? '#262626' : '#595959',
                                            marginBottom: 2
                                        }}>
                                            {milestone.label}
                                        </div>
                                        {targetRides ? (
                                            // Afficher la progression pour les paliers de courses
                                            <div>
                                                <div style={{ fontSize: 12, color: achieved ? '#52c41a' : '#1890ff', fontWeight: 600 }}>
                                                    {achieved ? (
                                                        'Objectif atteint'
                                                    ) : (
                                                        <>
                                                            {currentRides}/{targetRides} courses
                                                            <span style={{ color: '#8c8c8c', fontWeight: 400 }}>
                                                                {' '}({targetRides - currentRides > 0 ? `${targetRides - currentRides} restantes` : 'Objectif atteint!'})
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                                {!achieved && (
                                                    <Progress
                                                        percent={rideProgress}
                                                        size="small"
                                                        strokeColor={rideProgress >= 100 ? '#52c41a' : '#1890ff'}
                                                        showInfo={false}
                                                        style={{ marginTop: 4, width: '100%', maxWidth: 150 }}
                                                    />
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {achieved ? 'Objectif atteint' : 'En cours...'}
                                            </div>
                                        )}
                                    </div>

                                    {/* Montant */}
                                    <div style={{
                                        marginRight: 16,
                                        textAlign: 'right'
                                    }}>
                                        <div style={{
                                            fontWeight: 700,
                                            fontSize: 18,
                                            color: achieved ? (isDirect ? '#722ed1' : '#52c41a') : '#bfbfbf'
                                        }}>
                                            +{milestone.amount.toLocaleString()} F
                                        </div>
                                        {isDirect && milestone.affiliateAmount && (
                                            <div style={{
                                                fontSize: 11,
                                                color: '#8c8c8c',
                                                marginTop: 2
                                            }}>
                                                ({milestone.affiliateAmount} + {milestone.ambassadorAmount})
                                            </div>
                                        )}
                                    </div>

                                    {/* Status Tag */}
                                    <div style={{
                                        padding: '4px 12px',
                                        borderRadius: 16,
                                        background: statusConfig.bg,
                                        border: `1px solid ${statusConfig.border}`,
                                        color: statusConfig.color,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {statusConfig.icon}
                                        <span style={{ display: 'inline-block' }}>{statusConfig.text}</span>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Total */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px 20px',
                            background: '#f6ffed',
                            borderTop: '2px solid #b7eb8f'
                        }}>
                            <div>
                                <div style={{ fontWeight: 600, color: '#262626' }}>Total généré</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{achievedCount} palier{achievedCount > 1 ? 's' : ''} complété{achievedCount > 1 ? 's' : ''}</div>
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>
                                {earnedAmount.toLocaleString()} F
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DriverCommissionDetailPage;
